import { Button } from '../../Components/UI/button';
import MobileInput from '../../Components/Common/MobileInput';
import { validatorObj, isLive, getSalesMode, getAt99 } from '../../Utils/Utilities';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import SahiLoader from '../../Components/Common/SahiLoader';
import SplitText from './SplitText';
import useIsMobile from '../../Hooks/useIsMobile';
import PopupQuestions from './PopupQuestions';
import { Check } from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@radix-ui/react-hover-card';
import { cn } from '../../Lib/Cn';
import { getSubscriptionPlans } from '../../Utils/Utilities';
import { useDesktop } from '../../Components/Common/IsDesktop';
import { clearEverything } from '../../Utils/CookieHandler';

const MainLanding = ({
  isAuthenticated = false
}) => {
  const [userDetails, setUserDetails] = useState({
    mobile: '',
    countryCode: '91'
  });
  const [isVisible, setIsVisible] = useState(false);
  const [isCCLoaded, setIsCCLoaded] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Check for src=brochure and redirect
    const urlParams = new URLSearchParams(window.location.search);
    const srcParam = urlParams.get('src');
    const srcParamLower = srcParam ? srcParam.toLowerCase() : '';

    if (srcParam && (srcParamLower === 'brochure' || srcParamLower.startsWith('lc'))) {
      localStorage.setItem('SrcParam', srcParam);
      window.location.href = `/Web/login`;
      return;
    }

    if (srcParam && (srcParamLower.startsWith('lg') || srcParamLower.startsWith('rt'))) {
      localStorage.setItem('SrcParam', srcParam);
      // window.location.href = `/Web/download`;
      // return;
    }

    const fetchCountryCode = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        if (data && data.country_calling_code) {
          const cc = data.country_calling_code.replace('+', '');
          setUserDetails(prev => ({ ...prev, countryCode: cc }));
        }
      } catch (e) {
        setUserDetails(prev => ({ ...prev, countryCode: '91' }));
      } finally {
        setIsCCLoaded(true);
        setIsVisible(true);
      }
    };
    fetchCountryCode();
  }, []);
  // Function which will handle login
  const handleLogin = () => {
    if (!userDetails.mobile.trim()) {
      toast.error('Please enter a contact number.');
      return;
    } else if (!validatorObj.validateMobileByCC(userDetails.mobile, userDetails.countryCode)) {
      toast.error('Please enter a valid contact number.');
      return;
    }
    const combinedMobile = `${userDetails.countryCode}_${userDetails.mobile}`;
    const encodedMobile = encodeURIComponent(combinedMobile);

    // Store src to localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const srcParam = urlParams.get('src') || '';
    localStorage.setItem('SrcParam', srcParam);
    window.location.href = `/Web/login?i=${encodedMobile}`;
  };

  const handleLogout = () => {
    clearEverything();
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  if (!isCCLoaded) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <SahiLoader />
      </div>
    );
  }

  const features = [
    {
      title: "<b>Simpler</b> & <b>Smarter</b> than Excel",
      description: "Auto-import leads. No formulas, no confusion."
    },
    {
      title: "Multi-user Packages",
      description: "Assign leads and move deals with Kanban."
    },
    {
      title: "Desktop & Mobile Access",
      description: "Track calls, visits, and performance anywhere."
    },
    {
      title: "Smart Customer Insights",
      description: "Spot inactive customers, analyze rejections, boost repeat sales."
    }
  ];

  const commonFeatures = [
    // '<b>Simpler</b> & <b>Smarter</b> than Excel',
    // '<b>Multi-User</b> Packages',
    // '<b>Mobile</b> & <b>Desktop</b> Access',
    // '<b>Growth CRM</b>',
    // '<b>Team</b> & <b>Tasks</b>',
    // '<b>B2B Network<b>',
  ];

  const scrollToSec = () => {
    scrollToSection('pkgAndPricing');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  return (
    <>
      {isMobile ?
        (<section className="pt-16 md:py-32 px-4 md:px-6 h-auto">
          {/* (<section className="pt-16 md:py-32 px-4 md:px-6 h-[calc(100vh-56px-76px)]"> */}

          <div className="max-w-7xl mx-auto">
            <div className={`flex flex-col md:grid md:grid-cols-2 items-center text-center md:text-left transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Left Side - Text Content */}
              <div className="space-y-4 text-center md:text-left mb-8 md:mb-12">
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-sahi-dark-blue leading-[1.35] lg:leading-[1.5]">
                  Online <span className="text-sahi-green">Community</span> for <span className="text-sahi-green">Smart</span> Businesses
                </h1>


              </div>

              <div className="w-[100%] flex justify-center md:justify-end mx-2 mb-8 md:mb-16">
                <div className="w-full">
                  <img
                    src={isLive() ? "/Web/assets/Images/dashboard-preview.png" : "/assets/Images/mobile-dashboard.png"}
                    alt="Dashboard Preview"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Right Side - Mobile Number Entry Box */}
              {!isAuthenticated && (
                <div className="bg-sahi-disable border-2 mb-8 md:mb-16 rounded-xl p-6 md:p-8 w-full max-w-sm sm:max-w-md mx-auto md:ml-auto md:mr-0 flex flex-col justify-center">
                  {/* Logo */}
                  <div className="hidden md:flex justify-center mb-12">
                    <div className="md:w-64 w-32">
                      <img
                        src={isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"}
                        alt="SahiClub Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  </div>

                  <h2 className="text-base text-bold md:text-base mb-4 text-center text-sahi-dark-blue">
                    Enter Mobile to Get Started
                  </h2>

                  <div className="mb-4 flex justify-center">
                    <div className="w-[80%] md:w-[300px]">
                      <MobileInput
                        id="loginDialog"
                        value={userDetails.mobile}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) {
                            setUserDetails(prev => ({ ...prev, mobile: val }));
                          }
                        }}
                        placeholder="e.g. 9876543210"
                        className="w-full"
                        selectedCountryCode={userDetails.countryCode}
                        onCountryCodeChange={(newCC) =>
                          setUserDetails((prev) => ({ ...prev, countryCode: newCC }))
                        }
                        autoFocus={true}
                        onKeyPress={handleKeyPress}
                      />
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-row gap-3 justify-center items-center w-full">
                    <Button
                      onClick={handleLogin}
                      className="bg-sahi-green w-[44%] md:w-[200px] px-8 py-3 rounded-lg font-medium text-base shadow-lg"
                    >
                      Login
                    </Button>
                    <Button
                      onClick={handleLogin}
                      className="bg-sahi-green w-[44%] md:w-[200px] px-8 py-3 rounded-lg font-medium text-base shadow-lg"
                    >
                      Start Now
                    </Button>
                  </div>
                </div>
              )}

              {commonFeatures.length > 0 && <div className="max-w-fit justify-center mx-auto flex flex-col items-center lg:items-center mb-16">
                <ul className={cn(isMobile ? "flex flex-col justify-start space-y-2" : "flex flex-col justify-start gap-2")}>
                  {commonFeatures.map((feature, index) => (
                    <li key={index} className={cn("flex items-center gap-2 transition-all duration-500")}>
                      <div className='flex items-center gap-3 transition-all duration-500 flex-shrink-0 mt-1'>
                        <div className="w-6 h-6 rounded-full bg-sahi-green-light flex items-center justify-center">
                          <Check className="w-4 h-4 text-sahi-green" strokeWidth={3} />
                        </div>
                        <div className='text-base md:text-md text-gray-700 leading-relaxed whitespace-pre-line lg:whitespace-normal' dangerouslySetInnerHTML={{ __html: feature }}>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>}

              {isAuthenticated && (
                <div className="flex flex-col mb-16 items-center justify-center gap-4 w-full">
                  <Button
                    onClick={scrollToSec}
                    className="bg-sahi-green w-[80%] md:w-[200px] px-8 py-3 rounded-lg font-medium text-base shadow-lg"
                  >
                    Start Now
                  </Button>
                  <Button
                    onClick={handleLogout}
                    className="bg-white border border-sahi-green text-sahi-green w-[80%] md:w-[200px] px-8 py-3 rounded-lg font-medium text-base"
                  >
                    Change Mobile No.
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>)
        :
        (<section className="flex flex-col justify-between pt-14 md:pt-0 pb-8 px-4 md:px-6 h-auto"
          style={{ minHeight: 'calc(100vh - 100px)' }}
        >
          <div className="max-w-[83%] mx-auto flex-1 flex flex-col">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

              {/* Centered Heading */}
              <div className="text-center mb-6 md:mb-20 mt-16">
                {/* <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-sahi-dark-blue">
                  Simple <span className="text-sahi-green">CRM</span> to Increase <span className="text-sahi-green">Sales</span>
                </h1> */}
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-sahi-dark-blue">
                  Online <span className="text-sahi-green">Community</span> for <span className="text-sahi-green">Smart</span> Businesses
                </h1>
              </div>

              {/* Two Column Layout - Dashboard Image & Signup Form */}
              <div className="flex flex-col md:flex-row gap-6 md:gap-20 items-center mb-6 mt-2">

                {/* Left Side - Dashboard Image */}
                <div className="w-[90%] flex justify-center md:justify-end pb-0">
                  <div className="w-full">
                    <img
                      src={isLive() ? "/Web/assets/Images/dashboard-preview.png" : "/assets/Images/dashboard-preview.png"}
                      alt="Dashboard Preview"
                      className="w-full h-auto"
                    />
                  </div>
                </div>

                {/* Right Side - Mobile Number Entry Box */}
                <div className="bg-sahi-disable border-2 rounded-xl p-6 md:p-8 md:px-0 w-full max-w-sm mx-auto md:ml-auto md:mr-0 flex flex-col justify-center items-center">
                  {/* Logo */}
                  <div className="hidden md:flex justify-center mb-4 mt-4">
                    <div className="md:w-44 w-32">
                      <img
                        src={isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"}
                        alt="SahiClub Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  </div>

                  <div className="max-w-fit justify-center mx-auto flex flex-col items-center lg:items-center">
                    <ul className={cn(isMobile ? "flex flex-col items-center space-y-2" : "flex flex-col justify-start gap-2")}>
                      {commonFeatures.map((feature, index) => (
                        <li key={index} className={cn("flex items-center gap-2 transition-all duration-500")}>
                          <div className='flex items-center gap-3 transition-all duration-500 flex-shrink-0 mt-1'>
                            <div className="w-6 h-6 rounded-full bg-sahi-green-light flex items-center justify-center">
                              <Check className="w-4 h-4 text-sahi-green" strokeWidth={3} />
                            </div>
                            <div className='text-base md:text-md text-gray-700 leading-relaxed whitespace-pre-line lg:whitespace-normal' dangerouslySetInnerHTML={{ __html: feature }}>

                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>


                  {!isAuthenticated && (
                    <>

                      {/* <div className="h-[2px] mx-12 my-6 bg-gradient-to-r from-transparent via-gray-300 to-transparent" /> */}
                      <div className="w-[75%] h-[2px] bg-gradient-to-r from-transparent via-gray-400 to-transparent my-8" />
                      <h2 className="text-base text-bold md:text-base mb-4 text-center text-sahi-dark-blue">
                        Enter Mobile to Get Started
                      </h2>

                      <div className="mb-4 flex justify-center">
                        <div className="w-[220px] md:w-[220px]">
                          <MobileInput
                            id="loginDialog"
                            value={userDetails.mobile}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^\d*$/.test(val)) {
                                setUserDetails(prev => ({ ...prev, mobile: val }));
                              }
                            }}
                            placeholder="e.g. 9876543210"
                            className="w-full"
                            selectedCountryCode={userDetails.countryCode}
                            onCountryCodeChange={(newCC) =>
                              setUserDetails((prev) => ({ ...prev, countryCode: newCC }))
                            }
                            autoFocus={true}
                            onKeyPress={handleKeyPress}
                          />
                        </div>
                      </div>

                      {/* CTA Buttons */}
                      <div className="flex flex-col md:flex-row gap-3 justify-center pb-3 items-center w-full">
                        <Button
                          onClick={handleLogin}
                          className="bg-sahi-green w-[85%] md:w-[150px] px-8 py-3 rounded-lg font-medium text-base shadow-lg"
                        >
                          Login
                        </Button>
                        <Button
                          onClick={handleLogin}
                          className="bg-sahi-green w-[85%] md:w-[150px] px-8 py-3 rounded-lg font-medium text-base shadow-lg"
                        >
                          {getSalesMode() === 0 ? 'Start Now' : 'Start Now'}
                        </Button>
                      </div>
                    </>)}

                  {isAuthenticated && (
                    // <Button
                    // 	onClick={scrollToSec}
                    // 	className="bg-sahi-green w-[44%] md:w-[200px] px-8 py-3 rounded-lg font-medium text-base shadow-lg mt-10 mb-2"
                    // >
                    // 	Start Now
                    // </Button>
                    <>

                      <div className="flex flex-col gap-4 justify-center items-center w-full mt-8 mb-3">
                        {getAt99 && <div class="md:w-[250px] bg-sahi-green-light w-full rounded-lg p-4 py-5 text-center">
                          <div class="flex items-center justify-center gap-2 mb-2">
                            <span class="text-md font-semibold text-sahi-green animate-pulse">
                              Flash Offer
                            </span>
                          </div>
                          <p class="text-md text-sahi-dark-blue mb-1 font-semibold">
                            Start First Year @ just
                          </p>
                          <div class="flex items-baseline justify-center gap-1 mt-2">
                            <span class=" text-gray-700 font-medium mr-1">
                              <span class="text-md text-gray-700 opacity-80 font-medium mr-1">
                                ₹
                              </span>
                              <span class="line-through text-lg font-semibold">1,200</span>
                            </span>
                            <span class=" font-bold text-sahi-dark-blue">
                              <span class="text-3xl text-gray-700 opacity-80 font-medium mr-1">
                                ₹
                              </span>
                              <span class="text-5xl">99</span>
                            </span>
                            <span class="text-md text-gray-700 font-medium">
                              +GST
                            </span>
                          </div>
                        </div>}

                        <Button
                          onClick={scrollToSec}
                          className="bg-sahi-green w-[85%] md:w-[250px] px-8 py-3 rounded-lg font-medium text-base shadow-lg"
                        >
                          Start Now
                        </Button>
                        <Button
                          onClick={handleLogout}
                          className="bg-white border border-sahi-green text-sahi-green w-[85%] md:w-[250px] px-8 py-3 rounded-lg font-medium text-base"
                        >
                          Change Mobile No.
                        </Button>
                      </div></>
                  )}
                </div>

              </div>
            </div>

            {/* Feature Cards - Bottom Section */}
            {/* <div className="flex flex-col md:flex-row gap-4 items-stretch justify-center">
			{features.map((feature, index) => (
				<div 
				key={index}
				className="bg-white border border-sahi-green/20 rounded-xl p-4 flex-1 max-w-xs hover:shadow-xl hover:shadow-black/5 transition-all duration-300 hover:border-sahi-green/50"
				>
				<div className="flex flex-row gap-2 justify-start items-start">
					<h3 className="text-lg font-bold text-sahi-dark-blue mb-0 select-none">
					{feature.title}
					</h3>
				</div>
				<div className="w-full h-px bg-gradient-to-r from-gray-200 to-transparent mb-1.5 rounded-md"></div>
				<p className="text-gray-600 text-xs leading-4 select-none">
					{feature.description}
				</p>
				</div>
			))}
			</div> */}


          </div>
        </section>)}

    </>
  );
};

export default MainLanding;